import { Button, makeStyles } from "@material-ui/core";
import { useSnackbar } from "notistack";
import React, { useCallback, useEffect, useState } from "react";

import { Dealer } from "../../../../dtos/dealer";
import { AjaxResult } from "../../../../enums/ajaxResult";
import { FormField } from "../../../shared/formField";
import { TextField } from "../../../shared/TextField";
import { ValidationErrors } from "../../../shared/validationErrors";
import { useApi } from "../../../useApi";

interface Props {
    dealer: Dealer;
    onEditSuccess: () => void;
    disabled: boolean;
}

export const DealerEditForm = ({
    dealer,
    onEditSuccess,
    disabled,
}: Props) => {
    const classes = useStyles();
    const { enqueueSnackbar } = useSnackbar();
    const { dealerApi } = useApi();
    const [loading, setLoading] = useState(false);

    const [dealerEdit, setDealerEdit] = useState(dealer);
    const [validationErrors, setValidationErrors] = useState<string[]>([]);

    useEffect(() => {
        setDealerEdit(dealer);
    }, [dealer]);

    const handleSave = useCallback(() => {
        if (!dealer) return;
        setValidationErrors([]);
        setLoading(true);
        dealerApi.updateDealer(dealer.id, { ...dealerEdit }).then(r => {
            if (r.result === AjaxResult.Success) {
                enqueueSnackbar("Successfully updated dealer configuration", { variant: "success" });
                onEditSuccess();
            } else {
                if (r.messages) {
                    setValidationErrors(r.messages);
                }
                enqueueSnackbar("Failed to update configuration for dealer", { variant: "error" });
            }
            setLoading(false);
        });
    }, [dealerApi, dealerEdit, dealer, enqueueSnackbar, onEditSuccess]);

    const editDisabled = disabled || loading;

    return (
        <>
            {validationErrors.length > 0 && (
                <div><ValidationErrors errors={validationErrors} /></div>
            )}

            <FormField label="Dealer Name" required>
                <TextField
                    placeholder="Dealer Name"
                    value={dealerEdit.name}
                    onChange={(event) => setDealerEdit({ ...dealerEdit, name: event.currentTarget.value })}
                    InputProps={{
                        inputProps: { className: classes.input },
                    }}
                    disabled={editDisabled}
                />
            </FormField>

            <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={editDisabled || dealerEdit.name === ""}
                className={classes.saveButton}
            >
                {loading ? "Saving..." : "Save"}
            </Button>
        </>
    );
};

const useStyles = makeStyles({
    input: {
        padding: "5px",
        width: "100%",
        maxWidth: "400px",
    },
    saveButton: {
        margin: "20px 0px 20px 0px",
    },
});
